import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Bookmark } from 'lucide-react';
import { CATEGORIES } from '../data/defaultWorks';
import { picture, CARD_WIDTHS, CARD_SIZES, GRID_SIZES } from '../utils/imageUrl';

/**
 * The portfolio as shelves: one horizontal row per craft, in the order the
 * studio lists its crafts. Replaces the filter-pill grid.
 *
 * Why shelves and not filters: every craft is on screen without a click, and
 * a row of round hoops reads like a shelf of them. The trade-off — sideways
 * scrolling is awkward with a mouse — is covered two ways: arrow buttons on
 * pointer devices, and "See all" which turns a shelf into a plain grid.
 *
 * A shelf with nothing in it does not render. That is what happens to
 * "Frames & keepsakes" until there are photographs of any.
 */

function ShelfCard({ work, onOpen, isSaved, onToggleSave, inGrid }) {
  const img = picture(work, 'image', CARD_WIDTHS, 768);
  return (
    <div
      className={`group flex flex-col gap-2.5 ${inGrid ? '' : 'flex-shrink-0 w-[220px] sm:w-[268px] snap-start'}`}
    >
      <div className="relative rounded-[1.25rem] overflow-hidden aspect-[4/5] bg-[#FAF2EB] border border-[#4E2C23]/12 shadow-card transition-shadow duration-300 group-hover:shadow-card-hover">
        <button
          type="button"
          onClick={() => onOpen(work)}
          className="absolute inset-0 w-full h-full p-0 border-0 bg-transparent cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E2725B]/60 focus-visible:ring-inset"
        >
          <span className="sr-only">View {work.title}</span>
          <img
            src={img.src}
            srcSet={img.srcSet}
            sizes={inGrid ? GRID_SIZES : CARD_SIZES}
            width={img.width}
            height={img.height}
            alt={work.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
          />
        </button>

        <button
          onClick={() => onToggleSave(work.id)}
          aria-pressed={isSaved}
          aria-label={isSaved ? `Remove ${work.title} from saved` : `Save ${work.title}`}
          className={`absolute top-3 right-3 z-10 p-2 rounded-full backdrop-blur-md shadow-sm transition-all ${
            isSaved
              ? 'bg-[#4E2C23] text-[#FFDAB9] border border-[#4E2C23]'
              : 'bg-[#FFFBF7]/90 text-[#745147] hover:text-[#4E2C23]'
          }`}
        >
          <Bookmark aria-hidden="true" className={`w-3.5 h-3.5 ${isSaved ? 'fill-[#FFDAB9]' : ''}`} />
        </button>
      </div>

      <div className="flex flex-col gap-0.5 px-0.5">
        <h3 className="font-90s text-[15px] font-bold text-[#4E2C23] leading-snug line-clamp-2">
          {work.title}
        </h3>
        <p className="text-xs text-[#745147] line-clamp-1">{work.materials || 'Made to order'}</p>
      </div>
    </div>
  );
}

function Shelf({ category, items, onOpen, savedIds, onToggleSave }) {
  const [expanded, setExpanded] = useState(false);
  const rowRef = useRef(null);

  const scrollBy = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <section id={`shelf-${category.id}`} aria-labelledby={`shelf-${category.id}-title`} className="space-y-4 scroll-mt-28">
      <div className="flex items-end justify-between gap-4 border-b border-[#4E2C23]/15 pb-2.5">
        <div className="min-w-0">
          <h3
            id={`shelf-${category.id}-title`}
            className="font-90s font-black text-[#4E2C23] text-2xl sm:text-[26px] tracking-tight leading-tight"
          >
            {category.name}
          </h3>
          {category.desc && (
            <p className="text-xs text-[#745147] mt-1 line-clamp-1">{category.desc}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!expanded && items.length > 3 && (
            /* Arrows only where there is a pointer to use them. On touch the
               row itself is the control. */
            <div className="hidden md:flex items-center gap-1">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label={`Scroll ${category.name} back`}
                className="w-9 h-9 rounded-full border border-[#4E2C23]/20 bg-[#FFFBF7] text-[#4E2C23] flex items-center justify-center hover:border-[#E2725B] hover:text-[#B84D37] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label={`Scroll ${category.name} forward`}
                className="w-9 h-9 rounded-full border border-[#4E2C23]/20 bg-[#FFFBF7] text-[#4E2C23] flex items-center justify-center hover:border-[#E2725B] hover:text-[#B84D37] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#B84D37] hover:text-[#9E5040] bg-transparent border-0 py-2 px-1 whitespace-nowrap"
          >
            {expanded ? 'Show less' : `See all ${items.length}`}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
          {items.map((w) => (
            <ShelfCard
              key={w.id}
              work={w}
              onOpen={onOpen}
              isSaved={savedIds.includes(w.id)}
              onToggleSave={onToggleSave}
              inGrid
            />
          ))}
        </div>
      ) : (
        /* Negative margin + padding lets the row bleed to the viewport edge on
           phones, so the last card peeks in and signals there is more. */
        <div
          ref={rowRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0 pb-2"
        >
          {items.map((w) => (
            <ShelfCard
              key={w.id}
              work={w}
              onOpen={onOpen}
              isSaved={savedIds.includes(w.id)}
              onToggleSave={onToggleSave}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function WorksShelves({ works, onOpenLightbox, savedIds = [], onToggleSave }) {
  const shelves = CATEGORIES.filter((c) => c.id !== 'all')
    .map((c) => ({
      category: c,
      items: works
        .filter((w) => w.category === c.id)
        .sort((a, b) => (a.featured === b.featured ? 0 : a.featured ? -1 : 1)),
    }))
    .filter((s) => s.items.length > 0);

  if (!shelves.length) {
    return (
      <div className="artisan-card max-w-md mx-auto px-8 py-12 text-center space-y-2">
        <h3 className="font-90s text-xl font-bold text-[#4E2C23]">Nothing on the shelves yet</h3>
        <p className="text-xs text-[#745147] leading-relaxed">
          Pieces added from the studio dashboard will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12 sm:space-y-14">
      {shelves.map(({ category, items }) => (
        <Shelf
          key={category.id}
          category={category}
          items={items}
          onOpen={onOpenLightbox}
          savedIds={savedIds}
          onToggleSave={onToggleSave}
        />
      ))}
    </div>
  );
}
